import { useCallback, useEffect, useState } from "react";

/**
 * Per-user "I've already seen this auto-suggestion" memory.
 *
 * Behavior:
 *  - The popup shows the *first* time we match a destination from a user
 *    prompt. After the user dismisses or opens it, we never auto-suggest it
 *    again for that user (until the user explicitly asks for it by name; see
 *    `chatRecommendationMatcher.ts`).
 *
 * Storage:
 *  - localStorage on the device, keyed by `user_id`. This is intentionally
 *    lightweight — it's a UX preference, not data we need to keep in sync
 *    across devices. Easy to upgrade to a server-side preference later by
 *    swapping the read/write here.
 *
 *  - Anonymous (no user_id) callers get an in-memory-only set so the popup
 *    still suppresses for the current session even before profile load.
 */
const STORAGE_PREFIX = "bodhi:seen-recommendations:";

function storageKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_PREFIX}${userId}`;
}

function readSet(userId: string | null | undefined): Set<string> {
  const key = storageKey(userId);
  if (!key) return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === "string"));
  } catch {
    return new Set();
  }
}

function writeSet(userId: string | null | undefined, ids: Set<string>): void {
  const key = storageKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {
    // Storage may be unavailable (private mode / quota) — silently degrade
    // to session-only memory; the in-state Set still suppresses re-triggers.
  }
}

export function useSeenRecommendations(userId: string | null | undefined) {
  const [seenIds, setSeenIds] = useState<Set<string>>(() => readSet(userId));

  // When the user changes (login/logout/profile load), re-hydrate the set so
  // dismissals don't leak between accounts on a shared device.
  useEffect(() => {
    setSeenIds(readSet(userId));
  }, [userId]);

  const markSeen = useCallback(
    (id: string) => {
      setSeenIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        writeSet(userId, next);
        return next;
      });
    },
    [userId]
  );

  /** Forget a single recommendation (e.g. user explicitly asked again). */
  const forget = useCallback(
    (id: string) => {
      setSeenIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        writeSet(userId, next);
        return next;
      });
    },
    [userId]
  );

  /** Forget everything — useful for a future "reset suggestions" affordance. */
  const reset = useCallback(() => {
    setSeenIds(new Set());
    writeSet(userId, new Set());
  }, [userId]);

  return { seenIds, markSeen, forget, reset };
}
